// Partner (provider) side of the web app — the same endpoints the mobile app's provider dashboard uses.
import { api, qs } from "@/lib/api";

export const providerApi = {
  status: () => api("/provider/status"),
  modules: () => api("/modules", { auth: false }),
  declaration: () => api("/provider/declaration"),
  acceptDeclaration: (version) => api("/provider/declaration", { method: "POST", body: { version } }),
  draft: () => api("/provider/onboarding/draft"),
  saveDraft: (listing) => api("/provider/onboarding/draft", { method: "POST", body: listing }),
  deleteDraft: () => api("/provider/onboarding/draft", { method: "DELETE" }),
  myListings: () => api("/provider/my-listings"),
  createListing: (body) => api("/listings", { method: "POST", body }),
  patchListing: (id, body) => api(`/listings/${id}`, { method: "PATCH", body }),
  dashboard: (module) => api(`/provider/dashboard?${qs({ module })}`),
  schedule: (module) => api(`/provider/schedule?${qs({ module })}`),
  calendar: () => api("/provider/calendar"),
  patchBooking: (id, body) => api(`/provider/bookings/${id}`, { method: "PATCH", body }),
  earnings: (months = 6) => api(`/provider/earnings?${qs({ months })}`),
  entitlements: (module) => api(`/provider/entitlements${module ? `?module=${module}` : ""}`),
  connectStatus: () => api("/provider/connect/status"),
  connectOnboard: (return_base) => api("/provider/connect/onboard", { method: "POST", body: { return_base } }),
  connectPayouts: () => api("/provider/connect/payouts"),
  resendVerification: () => api("/auth/verify-email/resend", { method: "POST" }),
  verifyEmail: (code) => api("/auth/verify-email", { method: "POST", body: { code } }),
};

export const billingApi = {
  plans: () => api("/plans", { auth: false }),
  checkout: (plan, return_base) => api("/billing/checkout", { method: "POST", body: { plan, return_base } }),
  status: (session_id) => api(`/billing/status/${encodeURIComponent(session_id)}`),
  cancel: () => api("/billing/cancel", { method: "POST" }),
  compare: () => api("/billing/compare"),
};

export const TIER_ORDER = ["growth", "pro", "fleet", "scale", "enterprise", "payg"];

/** Marketing copy for the partner ladder (money comes from /api/plans, never from here). */
export const TIER_COPY = {
  growth: { tagline: "For solo pros getting started", features: ["0% commission on bookings", "2 worlds included", "Up to 5 listings in every world", "Online bookings & calendar", "Automatic customer reminders"] },
  pro: { tagline: "For growing businesses", highlight: true, features: ["Everything in Growth", "Up to 25 listings in every world", "Analytics & insights", "Priority placement in search", "AI receptionist add-on (£14.70/mo)"] },
  fleet: { tagline: "Flat ceiling — no per-seat stings", features: ["Everything in Pro", "3 worlds included", "Unlimited listings and staff seats", "Team roles & permissions", "AI receptionist add-on (£14.70/mo)"] },
  scale: { tagline: "Run several sites from one place", features: ["Everything in Max", "5 locations included, then £24.70 each", "AI receptionist with 300 minutes a month", "500 texts a month", "Next-morning payouts from day one", "3 Fable Ops manager seats", "4 worlds included", "API, webhooks & priority support"] },
  enterprise: { tagline: "Run a brand or franchise", features: ["Everything in Scale", "Unlimited locations", "AI receptionist with 1,500 minutes a month", "1,500 texts a month", "10 Fable Ops manager seats", "Every world included", "Named account manager"] },
  payg: { tagline: "No monthly fee — you only pay when you earn", features: ["£0 monthly", "2.7% per digital booking", "Every world included", "Up to 3 listings in every world", "Cancel anytime"] },
};

export const BOOKING_STATUS = {
  requested: { label: "Requested", tone: "bg-amber-100 text-amber-800" },
  pending: { label: "Pending", tone: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", tone: "bg-sky-100 text-sky-800" },
  confirmed: { label: "Confirmed", tone: "bg-emerald-100 text-emerald-800" },
  active: { label: "In progress", tone: "bg-sky-100 text-sky-800" },
  completed: { label: "Completed", tone: "bg-muted text-foreground/70" },
  cancelled: { label: "Cancelled", tone: "bg-rose-100 text-rose-800" },
  declined: { label: "Declined", tone: "bg-rose-100 text-rose-800" },
  no_show: { label: "No-show", tone: "bg-rose-100 text-rose-800" },
  refunded: { label: "Refunded", tone: "bg-muted text-foreground/70" },
};

export const SLOT_MODULES = ["services", "education", "experiences"];
